import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'http://app.localtest.me:3001';

/**
 * Embeds the full visual page builder from user-web inside admin (same session via .localtest.me cookies).
 * Navigation (Documents, etc.) lives inside the iframe — no duplicate admin chrome here.
 */
const PageEditorEmbed: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const builderUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set('embed', '1');
    return `${APP_ORIGIN.replace(/\/$/, '')}/${currentLanguage}/page-editor?${params.toString()}`;
  }, [currentLanguage, searchParams]);

  useEffect(() => {
    setLoading(true);
  }, [builderUrl]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'background.default',
            zIndex: 1,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t('loading')}
          </Typography>
        </Box>
      ) : null}
      <Box
        component="iframe"
        title="Page editor"
        src={builderUrl}
        onLoad={() => setLoading(false)}
        sx={{
          flex: 1,
          width: '100%',
          height: '100%',
          minHeight: 0,
          border: 0,
          bgcolor: 'background.default',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default PageEditorEmbed;
